import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, ApiError } from '../lib/api'
import { SearchableSelect } from '../components/SearchableSelect'

/**
 * Operator control of the sending fleet. Add an MTA node, create pools, bring sending IPs
 * online, warm/quarantine/retire them — the way you switch off a bad Elastic IP and bring a
 * fresh one into rotation. The selector reads these live, so status changes take effect at once;
 * the SPF panel shows the record to publish so DNS stays aligned.
 */
export function AdminIps() {
  const qc = useQueryClient()
  const pools = useQuery({ queryKey: ['admin-pools'], queryFn: api.admin.ipPools })
  const ips = useQuery({ queryKey: ['admin-ips'], queryFn: api.admin.sendingIps })
  const nodes = useQuery({ queryKey: ['admin-nodes'], queryFn: api.admin.mtaNodes })
  const spf = useQuery({ queryKey: ['admin-spf'], queryFn: api.admin.spfRecord })
  const [error, setError] = useState<ApiError | null>(null)
  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['admin-ips'] }); qc.invalidateQueries({ queryKey: ['admin-spf'] })
    qc.invalidateQueries({ queryKey: ['admin-pools'] }); qc.invalidateQueries({ queryKey: ['admin-nodes'] })
  }

  // create-node form
  const [nHost, setNHost] = useState(''); const [nIp, setNIp] = useState('')
  const createNode = useMutation({ mutationFn: () => api.admin.createMtaNode({ hostname: nHost.trim(), privateIp: nIp.trim() }), onSuccess: () => { setNHost(''); setNIp(''); setError(null); invalidate() }, onError: e => setError(e as ApiError) })
  // create-pool form
  const [pName, setPName] = useState('')
  const createPool = useMutation({ mutationFn: () => api.admin.createIpPool({ name: pName.trim() }), onSuccess: () => { setPName(''); setError(null); invalidate() }, onError: e => setError(e as ApiError) })
  // add-ip form
  const [addr, setAddr] = useState(''); const [idx, setIdx] = useState(1); const [pool, setPool] = useState(''); const [node, setNode] = useState(''); const [status, setStatus] = useState('Warming')
  const addIp = useMutation({ mutationFn: () => api.admin.createSendingIp({ address: addr.trim(), ipIndex: idx, poolId: pool, nodeId: node, status }), onSuccess: () => { setAddr(''); setError(null); invalidate() }, onError: e => setError(e as ApiError) })
  const setIpStatus = useMutation({ mutationFn: (v: { address: string; status: string }) => api.admin.updateSendingIp(v.address, { status: v.status }), onSuccess: invalidate })
  const retire = useMutation({ mutationFn: (v: { address: string; release: boolean }) => api.admin.retireSendingIp(v.address, v.release), onSuccess: invalidate, onError: (e: any) => setError(e as ApiError) })
  const rdns = useMutation({
    mutationFn: (address: string) => api.admin.setRdns(address),
    onSuccess: (r) => { setError(null); alert(`Reverse DNS for ${r.address} → ${r.hostname} (${r.status}). Propagation can take a few minutes.`) },
    onError: (e: any) => setError(e as ApiError),
  })
  // Two removal modes: take out of rotation (keep the AWS EIP for delisting) or fully release it.
  function removeIp(address: string, index: number) {
    if (index <= 1) { alert('The shared primary IP cannot be removed.'); return }
    const choice = window.prompt(
      `Remove ${address}?\n\nType "rotation" to take it out of sending but keep the AWS IP (delist then re-add),\n` +
      `or "release" to also release the Elastic IP back to AWS (permanent).`, 'rotation')
    if (choice === 'rotation') retire.mutate({ address, release: false })
    else if (choice === 'release') { if (confirm(`Permanently release ${address} back to AWS? This cannot be undone.`)) retire.mutate({ address, release: true }) }
  }

  return (
    <div>
      <div className="admin-head">
        <div>
          <span className="op-badge">Operator</span>
          <h1 style={{ marginTop: 8 }}>Sending fleet</h1>
          <p className="lede" style={{ marginTop: 4 }}>MTA nodes, IP pools and sending IPs. Changes take effect immediately — the selector reads these rows live.</p>
        </div>
      </div>
      {error && <div className="alert error" style={{ marginBottom: 16 }}>{error.message}</div>}

      <h2>Sending IPs</h2>
      <div className="card flush" style={{ marginBottom: 16 }}>
        <table className="data">
          <thead><tr><th>Address</th><th>Idx</th><th>Status</th><th>Pool</th><th>Node</th><th>Transport</th><th>Rep</th><th /></tr></thead>
          <tbody>
            {(ips.data?.data ?? []).map((i: any) => (
              <tr key={i.address}>
                <td className="mono">{i.address}</td>
                <td className="mono">{i.ip_index}</td>
                <td>
                  <select value={i.status} onChange={e => setIpStatus.mutate({ address: i.address, status: e.target.value })}>
                    {['Active', 'Warming', 'Quarantined', 'Retired'].map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </td>
                <td className="small">{i.pool}</td>
                <td className="small mono">{i.node}</td>
                <td className="small mono">{i.transport}</td>
                <td className="small">{i.reputation ?? '—'}{i.blocklisted && <span className="badge failed">RBL</span>}</td>
                <td className="right">
                  {i.ip_index > 1 ? (
                    <div className="row" style={{ gap: 6, justifyContent: 'flex-end' }}>
                      <button className="ghost small" disabled={rdns.isPending} title="Set forward-confirmed reverse DNS (deliverability)"
                              onClick={() => rdns.mutate(i.address)}>Set rDNS</button>
                      <button className="ghost small danger" disabled={retire.isPending} onClick={() => removeIp(i.address, i.ip_index)}>Remove</button>
                    </div>
                  ) : <span className="small muted" title="The shared primary IP is managed separately">primary</span>}
                </td>
              </tr>
            ))}
            {(ips.data?.data ?? []).length === 0 && <tr><td colSpan={8} className="muted small" style={{ padding: 16 }}>No sending IPs yet.</td></tr>}
          </tbody>
        </table>
      </div>

      <div className="card" style={{ marginBottom: 24 }}>
        <h3 style={{ marginTop: 0 }}>Add sending IP</h3>
        <form className="row" style={{ gap: 8, flexWrap: 'wrap', alignItems: 'flex-end' }} onSubmit={e => { e.preventDefault(); addIp.mutate() }}>
          <div className="field" style={{ marginBottom: 0 }}><label>IP address</label><input value={addr} onChange={e => setAddr(e.target.value)} placeholder="54.x.x.x" className="mono" /></div>
          <div className="field" style={{ marginBottom: 0, width: 80 }}><label>Index</label><input type="number" value={idx} onChange={e => setIdx(Number(e.target.value))} /></div>
          <div className="field" style={{ marginBottom: 0, minWidth: 170 }}><label>Pool</label>
            <SearchableSelect value={pool} onChange={setPool}
              options={(pools.data?.data ?? []).map((p: any) => ({ value: p.id, label: p.name }))}
              placeholder="Select pool" searchPlaceholder="Search pools…" />
          </div>
          <div className="field" style={{ marginBottom: 0, minWidth: 190 }}><label>Node</label>
            <SearchableSelect value={node} onChange={setNode}
              options={(nodes.data?.data ?? []).map((n: any) => ({ value: n.hostname, label: n.hostname }))}
              placeholder="Select node" searchPlaceholder="Search nodes…" />
          </div>
          <div className="field" style={{ marginBottom: 0 }}><label>Status</label>
            <select value={status} onChange={e => setStatus(e.target.value)}>{['Warming', 'Active'].map(s => <option key={s}>{s}</option>)}</select>
          </div>
          <button type="submit" disabled={!addr || !pool || addIp.isPending}>Add IP</button>
        </form>
      </div>

      <div className="row" style={{ gap: 16, flexWrap: 'wrap', marginBottom: 24 }}>
        <div className="card" style={{ flex: 1, minWidth: 280 }}>
          <h3 style={{ marginTop: 0 }}>MTA nodes</h3>
          <ul className="small">{(nodes.data?.data ?? []).map((n: any) => <li key={n.hostname} className="mono">{n.hostname} — {n.status} {n.healthy ? '✓' : '⚠'}</li>)}</ul>
          <form className="row" style={{ gap: 8, alignItems: 'flex-end' }} onSubmit={e => { e.preventDefault(); createNode.mutate() }}>
            <div className="field" style={{ marginBottom: 0 }}><label>Hostname</label><input value={nHost} onChange={e => setNHost(e.target.value)} placeholder="mta2.itsbingmybusiness.com" /></div>
            <div className="field" style={{ marginBottom: 0 }}><label>Private IP</label><input value={nIp} onChange={e => setNIp(e.target.value)} placeholder="172.31.x.x" className="mono" /></div>
            <button type="submit" disabled={!nHost || createNode.isPending}>Add node</button>
          </form>
        </div>
        <div className="card" style={{ flex: 1, minWidth: 280 }}>
          <h3 style={{ marginTop: 0 }}>IP pools</h3>
          <ul className="small">{(pools.data?.data ?? []).map((p: any) => <li key={p.id}>{p.name} — {p.kind} ({p.ip_count} IPs){p.is_default ? ' · default' : ''}</li>)}</ul>
          <form className="row" style={{ gap: 8, alignItems: 'flex-end' }} onSubmit={e => { e.preventDefault(); createPool.mutate() }}>
            <div className="field" style={{ marginBottom: 0 }}><label>Pool name</label><input value={pName} onChange={e => setPName(e.target.value)} placeholder="shared-2" /></div>
            <button type="submit" disabled={!pName || createPool.isPending}>Add pool</button>
          </form>
        </div>
      </div>

      <h2>SPF record</h2>
      <div className="card">
        <p className="small muted">Publish this TXT at <span className="mono">{spf.data?.host}</span> whenever the fleet changes so customer SPF includes resolve to the current IPs ({spf.data?.ip_count} live).</p>
        <pre className="code" style={{ whiteSpace: 'pre-wrap' }}>{spf.data?.value}</pre>
      </div>
    </div>
  )
}
